import React, { useEffect } from 'react';
import { IStore } from "../../../redux/reducers";
import { useDispatch, useSelector } from "react-redux";
import * as JobActions from '../../../redux/actions/jobAction';
import { RefreshControl } from 'react-native';
import { JobStatus, IUserJobListItem, ISearchQuery, IDateRange, IJob } from '../../../models';
import { Box, FlatList, Button, HStack, useToast } from 'native-base';
import { listStyle } from 'src/styles/listStyle';
import { downloadJobListReport, downloadScannedImageFromId } from 'src/services/download';
import UserJobHistoryItem from 'src/components/listItem/UserJobHistoryItem';
import { isAveragingJob } from 'src/utils/jobUtils';
import { isElectron } from 'src/utils/miscUtils';
import MaterialIconByName from 'src/components/MaterialIconByName';
import { getText } from 'src/utils/i18n';
import * as GrainAPI from 'src/api/grain';
import {
  isSameGrainJobReportError,
  resolveJobReportDateFilter,
  validateJobReportGrainIds
} from 'src/utils/jobListReportFilters';

interface IUserJobHistoryListProps {
  navigation: {
    navigate: (route: string, params?: Record<string, unknown>) => void;
  };
  pageType: string; // tabs or single
}

export default function UserJobHistoryList(props: IUserJobHistoryListProps) {

  const [listData, setListData] = React.useState<IUserJobListItem[]>([]);
  const [localJobProgressById, setLocalJobProgressById] = React.useState<Record<string, {
    percent: number;
    message?: string;
    stage?: string;
    updatedAt: number;
  }>>({});
  const [localProfileNamesByKey, setLocalProfileNamesByKey] = React.useState<Record<string, string>>({});
  const [downloadingAll, setDownloadingAll] = React.useState(false);

  const [refreshing, _setRefreshing] = React.useState(false);
  const userJobs = useSelector((store: IStore) => store.job.userJobs.jobs);
  const username = useSelector((store: IStore) => store.user.userProfile.response?.username);
  const isOnline = useSelector((store: IStore) => store.app.isOnline);
  const dispatch = useDispatch();
  const toast = useToast();

  // Averaging feature selectors
  const isSelectionMode = useSelector((store: IStore) => store.job.isSelectionMode);
  const selectedJobIds = useSelector((store: IStore) => store.job.selectedJobIds);
  const profileLookupGrainIds = React.useMemo(() => {
    const jobs = userJobs?.Items?.filter((item): item is IJob => item !== null && item !== undefined) || [];
    return Array.from(new Set(jobs.map((job) => job.grainId).filter(Boolean))).sort();
  }, [userJobs]);
  const profileLookupGrainIdsKey = profileLookupGrainIds.join('|');

  useEffect(() => {
    if (!isElectron() || typeof window === 'undefined') {
      return;
    }

    const electron = (window as any).electron;
    if (!electron?.onJobProgress) {
      return;
    }

    const unsubscribe = electron.onJobProgress((data: any) => {
      const jobId = typeof data?.jobId === 'string' ? data.jobId : undefined;
      if (!jobId) return;
      const percent = typeof data?.percent === 'number' ? data.percent : undefined;
      if (percent === undefined || !Number.isFinite(percent)) return;

      const message = typeof data?.message === 'string' ? data.message : undefined;
      const stage = typeof data?.stage === 'string' ? data.stage : undefined;

      setLocalJobProgressById((prev) => {
        const existing = prev[jobId];
        const nextPercent = Math.max(existing?.percent ?? 0, Math.max(0, Math.min(99, Math.round(percent))));
        const nextMessage = message?.trim() ? message.trim() : existing?.message;
        const nextStage = stage?.trim() ? stage.trim() : existing?.stage;
        return {
          ...prev,
          [jobId]: {
            percent: nextPercent,
            message: nextMessage,
            stage: nextStage,
            updatedAt: Date.now()
          }
        };
      });
    });

    return () => {
      if (typeof unsubscribe === 'function') {
        unsubscribe();
      }
    };
  }, []);

  useEffect(() => {
    if (!isElectron() || typeof window === 'undefined') {
      return;
    }

    const electron = (window as any).electron;
    if (!electron?.getGrainProfiles) {
      return;
    }

    electron.getGrainProfiles()
      .then((profiles: any[]) => {
        if (!Array.isArray(profiles)) return;
        const next: Record<string, string> = {};
        profiles.forEach((profile) => {
          if (profile?.grainId && profile?.profileId && profile?.profileName) {
            next[`${profile.grainId}:${profile.profileId}`] = profile.profileName;
          }
        });
        setLocalProfileNamesByKey(next);
      })
      .catch((error: Error) => {
        console.warn('Failed to load local profile names for history:', error);
      });
  }, [username, userJobs]);

  useEffect(() => {
    if (isOnline === false) {
      return;
    }

    const grainIds = profileLookupGrainIdsKey ? profileLookupGrainIdsKey.split('|') : [];
    if (grainIds.length === 0) {
      return;
    }

    let cancelled = false;

    Promise.all(grainIds.map((grainId) =>
      GrainAPI.getGrainProfiles(String(grainId)).catch(() => [])
    ))
      .then((profileGroups) => {
        if (cancelled) return;
        const next: Record<string, string> = {};
        profileGroups.flat().forEach((profile) => {
          if (profile?.grainId && profile?.profileId && profile?.profileName) {
            next[`${profile.grainId}:${profile.profileId}`] = profile.profileName;
          }
        });
        setLocalProfileNamesByKey((current) => ({ ...current, ...next }));
      });

    return () => {
      cancelled = true;
    };
  }, [isOnline, profileLookupGrainIdsKey]);

  // Handle toggle selection
  const handleToggleSelection = (jobId: string) => {
    if (selectedJobIds.includes(jobId)) {
      dispatch(JobActions.deselectJob(jobId));
    } else {
      dispatch(JobActions.selectJob(jobId));
    }
  };

  useEffect(() => {
    fetchUserJobs();
    pollUserJobs();
  }, [username]);

  const extractVariety = (info: unknown) => {
    if (!info || typeof info !== 'object') {
      return 'Default';
    }
    const infoRecord = info as Record<string, unknown>;
    return typeof infoRecord.variety === 'string' && infoRecord.variety ? infoRecord.variety : 'Default';
  };

  useEffect(() => {
    const listItems: IUserJobListItem[] = []
    userJobs?.Items
      .filter((item): item is IJob => item !== null && item !== undefined)
      .filter((item) => isDisplayableJob(item))
      .forEach((item) => {
        listItems.push({
          "jobId": item.jobId,
          "jobStatus": item.jobStatus,
          "grainId": item.grainId,
          "profileId": item.profileId,
          "createdAt": item.createdAt,
          "updatedAt": item.updatedAt,
          "processingMode": item.processingMode,
          "syncStatus": item.syncStatus,
          "syncError": item.syncError,
          "syncedAt": item.syncedAt,
          "variety": extractVariety(item.info),
          "mlLabelingStatus": item.mlLabelingStatus
        })
      })
    setListData(listItems)
  }, [userJobs]);

  useEffect(() => {
    setLocalJobProgressById((prev) => {
      const next = { ...prev };
      let changed = false;

      const jobs = userJobs?.Items?.filter((item): item is IJob => item !== null && item !== undefined) || [];
      const byId = new Map<string, IJob>();
      jobs.forEach((job) => job?.jobId && byId.set(job.jobId, job));

      Object.keys(next).forEach((jobId) => {
        const job = byId.get(jobId);
        if (!job) {
          delete next[jobId];
          changed = true;
          return;
        }
        const status = job.jobStatus as JobStatus | undefined;
        const terminal = status === JobStatus.JOB_COMPLETED || status === JobStatus.JOB_FAILED;
        if (job.processingMode !== 'local' || terminal) {
          delete next[jobId];
          changed = true;
        }
      });

      return changed ? next : prev;
    });
  }, [userJobs]);

  const pollUserJobs = async () => {
    if (username !== undefined) {
      dispatch(JobActions.pollUserJobs.request({
        'username': username
      }))
    }
  }

  const fetchUserJobs = async () => {
    if (username !== undefined) {
      dispatch(JobActions.userJobs.request({
        'username': username
      }))
    }
  }

  const isDisplayableJob = (job: IJob) => {
    const status = job.jobStatus;
    if (status === JobStatus.JOB_IMAGE_SCANNING || status === JobStatus.JOB_IMAGE_UPLOADING || status === JobStatus.JOB_IMAGE_SCANNED) {
      return job.processingMode === 'local';
    }
    return [
      JobStatus.JOB_IMAGE_UPLOADED,     // Cloud: show after upload
      JobStatus.JOB_PROCESSING_LOCAL,   // Local: during processing
      JobStatus.JOB_IN_PROGRESS,        // Cloud: during processing
      JobStatus.JOB_SYNCING,
      JobStatus.JOB_COMPLETED,
      JobStatus.JOB_FAILED
    ].includes(status);
  };

  const grainTypes = useSelector((store: IStore) => store.user.userProfile.response?.grains);
  const grainIdFilter = useSelector((store: IStore) => store.app.grainIdFilter) || "all";
  const searchQuery: ISearchQuery | undefined = useSelector((store: IStore) => store.app.searchQuery);
  const dateRange: IDateRange | undefined = useSelector((store: IStore) => store.app.dateRange);
  const colorMode = useSelector((store: IStore) => store.settings.colorMode);
  const locale = useSelector((store: IStore) => store.app.locale);
  useEffect(() => {
    updateJobList();
  }, [grainIdFilter, searchQuery, dateRange, userJobs, grainTypes]);

  useEffect(() => {
    if (dateRange && dateRange.from && dateRange.to) {
      const localFiltered = userJobs?.Items
        ?.filter((item): item is IJob => item !== null && item !== undefined)
        .filter((item: IJob) =>
          item.updatedAt !== undefined && item.updatedAt >= dateRange.from && item.updatedAt <= dateRange.to
        );
      if ((!localFiltered || localFiltered.length === 0) && username) {
        dispatch(JobActions.userJobs.request({
          username,
          from: dateRange.from,
          to: dateRange.to
        }));
      }
    }
  }, [dateRange, userJobs, username]);

  const updateJobList = () => {
    const listItems: IUserJobListItem[] = []
    userJobs?.Items
      .filter((item): item is IJob => item !== null && item !== undefined)
      .filter((item) => isDisplayableJob(item))
      .filter((item) => filterByGrainId(item.grainId))
      .filter((item: IJob) => filterBySearchQuery(item))
      .filter((item: IJob) => filterByDateRange(item))
      .forEach((item) => {
        listItems.push({
          "jobId": item.jobId,
          "jobStatus": item.jobStatus,
          "grainId": item.grainId,
          "profileId": item.profileId,
          "createdAt": item.createdAt,
          "updatedAt": item.updatedAt,
          "processingMode": item.processingMode,
          "syncStatus": item.syncStatus,
          "syncError": item.syncError,
          "syncedAt": item.syncedAt,
          "variety": extractVariety(item.info),
          "mlLabelingStatus": item.mlLabelingStatus
        })
      })
    setListData(listItems)
  }

  const filterByGrainId = (id?: string) => {
    if (grainIdFilter === undefined
      || grainIdFilter === ""
      || grainIdFilter === "0"
      || grainIdFilter === "all") {
      return true
    }
    return id === grainIdFilter
  }

  const filterBySearchQuery = (item: IJob) => {
    if (searchQuery === undefined || searchQuery?.query === undefined || searchQuery?.query === "") {
      return true
    }

    if (searchQuery.type === 'jobId') {
      return item.jobId.includes(searchQuery.query)
    }
    return false
  }

  const filterByDateRange = (item: IJob) => {
    if (dateRange === undefined || dateRange?.from === undefined || dateRange?.to === undefined) {
      return true
    }

    return item.updatedAt !== undefined && item.updatedAt >= dateRange.from && item.updatedAt <= dateRange.to;
  }

  const downloadFilteredJobs = async () => {
    if (!username) {
      return;
    }

    if (!validateJobReportGrainIds(listData).isValid) {
      toast.show({
        title: getText('downloadFailed'),
        description: getText('jobListReportSameGrainRequired'),
        duration: 5000,
        bg: 'error.600'
      });
      return;
    }

    setDownloadingAll(true);
    try {
      const downloaded = await downloadJobListReport({
        scope: { type: 'user', username },
        filters: {
          ...(grainIdFilter !== 'all' ? { grainId: grainIdFilter } : {}),
          ...(searchQuery?.type === 'jobId' && searchQuery.query ? { jobId: searchQuery.query } : {}),
          ...resolveJobReportDateFilter(dateRange, 'updatedAt'),
          statuses: [
            JobStatus.JOB_IMAGE_SCANNING,
            JobStatus.JOB_IMAGE_UPLOADING,
            JobStatus.JOB_IMAGE_SCANNED,
            JobStatus.JOB_IMAGE_UPLOADED,
            JobStatus.JOB_PROCESSING_LOCAL,
            JobStatus.JOB_IN_PROGRESS,
            JobStatus.JOB_SYNCING,
            JobStatus.JOB_COMPLETED,
            JobStatus.JOB_FAILED
          ]
        },
        deviceTimezone: Intl.DateTimeFormat().resolvedOptions().timeZone || 'Asia/Kolkata',
        locale,
        output_format: 'xls'
      });
      if (!downloaded) {
        return;
      }
      toast.show({
        title: getText('downloadSuccessful'),
        description: getText('jobListReportDownloadSuccess'),
        duration: 3000
      });
    } catch (error) {
      toast.show({
        title: getText('downloadFailed'),
        description: getText(
          isSameGrainJobReportError(error)
            ? 'jobListReportSameGrainRequired'
            : 'jobListReportDownloadFailed'
        ),
        duration: 5000
      });
    } finally {
      setDownloadingAll(false);
    }
  };

  const deleteJob = (jobId: string) => {
    dispatch(JobActions.deleteJob.request(jobId));
    setListData(prev => prev.filter(job => job.jobId !== jobId));
  };
  const handleItemClick = (item: IUserJobListItem) => {
    // In selection mode, clicking toggles selection instead of navigating
    if (isSelectionMode) {
      handleToggleSelection(item.jobId);
      return;
    }

    // Set the job ID and fetch full job details via API
    dispatch(JobActions.setJobId(item.jobId));
    dispatch(JobActions.jobStatus.request(item.jobId));
    dispatch(JobActions.pollJobStatus.request(item.jobId));

    if (props.pageType === 'tabs') {
      // Navigate to Analysis tab with jobId as query param
      props.navigation.navigate('Analysis', { jobId: item.jobId });
    } else {
      props.navigation.navigate('Results', { jobId: item.jobId });
    }
  };
  const renderItem = ({ item }:
    { item: IUserJobListItem }
  ) => {
    const job = userJobs?.Items?.find((j): j is IJob => !!j && j.jobId === item.jobId);
    const isAveraged = isAveragingJob(job);
    const localProgress = item.processingMode === 'local' ? localJobProgressById[item.jobId] : undefined;

    return (
      <UserJobHistoryItem
        item={item}
        downloadImageCallback={async () => {
          await downloadScannedImageFromId(item.jobId);
        }}
        itemCallback={() => handleItemClick(item)}
        retryJobCallback={() => {
          if (job?.processingMode === 'local') {
            const inputImagePath = (job as unknown as { inputImagePath?: string })?.inputImagePath;
            if (!inputImagePath) {
              return;
            }
            const additionalAttributes = (job as unknown as { additionalAttributes?: Record<string, unknown> })
              ?.additionalAttributes;
            const derivedSource =
              typeof additionalAttributes?.source === 'string' ? additionalAttributes.source : undefined;
            dispatch(
              JobActions.processLocalJob.request({
                jobId: item.jobId,
                grainId: job.grainId,
                profileId: job.profileId,
                additionalAttributes: job.additionalAttributes,
                info: job.info ?? null,
                source: job.source || derivedSource || 'desktop',
                location: job.location || '',
                productName: job.productName,
                imagePath: inputImagePath
              })
            );
            return;
          }

          dispatch(JobActions.queueJob.request({ jobId: item.jobId }));
        }}
        deleteJobCallback={deleteJob}
        isSelectionMode={isSelectionMode}
        isSelected={selectedJobIds.includes(item.jobId)}
        onToggleSelection={handleToggleSelection}
        isAveraged={isAveraged}
        localProfileName={localProfileNamesByKey[`${item.grainId}:${item.profileId}`]}
        localProgressPercent={localProgress?.percent}
        localProgressMessage={localProgress?.message}
      />
    );
  };

  const renderFooter = () => {
    return (
      <Box style={listStyle.footer}>
        <Button onPress={() => {
          fetchUserJobs()
        }} isLoading={refreshing} >Load more</Button>
      </Box>
    );
  };

  return (
    <Box flex={1} bg={colorMode === 'light' ? 'primary.50' : 'primary.900'}>
      <HStack justifyContent="flex-end" px={2} py={2}>
        <Button
          startIcon={<MaterialIconByName name="file-excel" />}
          onPress={downloadFilteredJobs}
          isLoading={downloadingAll}
          isLoadingText={getText('generatingReport')}
          isDisabled={!username}
        >
          {getText('downloadAll')}
        </Button>
      </HStack>
      <FlatList
        data={[...listData].sort((a, b) => (b.updatedAt ?? 0) - (a.updatedAt ?? 0))}
        renderItem={renderItem}
        keyExtractor={item => item.jobId}
        onEndReached={fetchUserJobs}
        ListFooterComponent={renderFooter}
        onEndReachedThreshold={0.3}
        refreshControl={
          <RefreshControl refreshing={refreshing} onRefresh={() => {
            fetchUserJobs();
          }} />
        }
      />
    </Box>
  );
};
